/**
 * ⚠ DEFAULT OFF since 2026-05-22 (re-enable per-client via localStorage
 * 'voiceConnQualitySuppress'='on'). The auto-suppress below false-positived on
 * GOOD links — LiveKit ConnectionQuality flaps to Poor especially when the tab
 * is BACKGROUNDED (timer + WebRTC send-pacer throttling → bursty loss + stale
 * stats) — and the resulting kill→restore camera loop (black screen + "Слабый
 * интернет"/"Связь восстановилась" toast spam) was worse than the problem it
 * solved. Team-call evidence: Aidar had 0% loss + 14-73 Mbps up yet his camera
 * was suppressed; Dmitriy (24 cores, 34 Mbps up) only lost audio packets while
 * tabbed away. Audio is now protected at the SOURCE (720p@1.2 Mbps default cap
 * + degradationPreference:'balanced', 108A) + LiveKit BWE, so killing the
 * camera is redundant. Same family as the disabled CPU video governor (#41) and
 * video auto-recover (#46). Code kept (gated) for opt-in. See invariant #48.
 *
 * Auto-suppress video / screen share when the local participant's
 * LiveKit connection quality drops to Poor, so audio always survives
 * even on a 50-100 kbps connection. The "20-person call, two users
 * can't hear or be heard" pattern was always those users trying to
 * publish 200+ kbps of video on a 100 kbps pipe — by the time audio
 * packets fought for what's left, both directions were lossy enough
 * to be unintelligible.
 *
 * Behaviour matrix (decided 2026-05-17 with the CTO):
 *
 *   ConnectionQuality stays Excellent / Good
 *     → nothing; tracks live as published
 *
 *   ConnectionQuality === Poor for ≥ POOR_DEBOUNCE_MS (5 s)
 *     → unpublish camera if active
 *     → block screen-share start (gating lives in the relevant store)
 *     → auto-stop active screen share
 *     → broadcast `{ bandwidthSuppressed: ['video', 'screen'] }` via
 *       participant metadata so peer tiles can show the cause
 *     → fire local UI signal (toast / banner) via the optional
 *       `onLocalSuppress` callback
 *
 *   ConnectionQuality === Lost
 *     → same as Poor, no debounce — instant suppression. LiveKit's
 *       own reconnect logic still runs in parallel.
 *
 *   ConnectionQuality recovers to Good / Excellent for ≥ GOOD_DEBOUNCE_MS
 *     (30 s, gated against flapping)
 *     → auto re-enable camera if it WAS on before suppression
 *     → screen share is NOT auto-restored (it was a user-driven
 *       intentional act — user re-clicks if they want it back)
 *     → clear metadata flag
 *
 * Hysteresis numbers:
 *   • 5 s before suppressing avoids tearing down on a 2-second WiFi
 *     hiccup. LiveKit's own quality estimator already smooths over
 *     ~1 s, so 5 s upstream of that is generous.
 *   • 30 s before restoring avoids yo-yo: a user pacing between
 *     rooms in a marginal-signal area would otherwise cycle on/off
 *     every 10 s.
 *   • Lost has no debounce because by the time LiveKit flips a
 *     participant to Lost the data plane is already gone; waiting
 *     5 s here would only mean 5 s of doomed publish attempts.
 *
 * Lifecycle: one watcher per Room. Created on Room connect, destroyed
 * on disconnect. NOT global — concurrent calls (channel + DM)
 * each get their own.
 */

import { Room, ConnectionQuality, RoomEvent, Track, type LocalTrackPublication } from 'livekit-client'

const POOR_DEBOUNCE_MS = 5_000
const GOOD_DEBOUNCE_MS = 30_000

export interface ConnectionQualityWatcherOptions {
  /**
   * Fired when we just transitioned into the suppressed state.
   * Caller wires this to a toast / banner / sound. Pass a reason
   * string the UI can show ("Poor connection" / "Lost connection").
   */
  onLocalSuppress?: (reason: 'poor' | 'lost') => void
  /**
   * Fired when we just left the suppressed state. Caller can show a
   * "connection restored" toast; the watcher already auto-re-enabled
   * the camera if it was on before.
   */
  onLocalRestore?: () => void
}

interface SuppressionRecord {
  /** Was the user's camera on at the moment of suppression? */
  cameraWasOn: boolean
  /** Was screen share active at suppression? (kept for telemetry only — we don't auto-restore screens) */
  screenWasOn: boolean
}

export class ConnectionQualityWatcher {
  private readonly room: Room
  private readonly opts: ConnectionQualityWatcherOptions
  private poorTimer: ReturnType<typeof setTimeout> | null = null
  private goodTimer: ReturnType<typeof setTimeout> | null = null
  private suppressed: SuppressionRecord | null = null
  private destroyed = false

  constructor(room: Room, opts: ConnectionQualityWatcherOptions = {}) {
    this.room = room
    this.opts = opts
    this.room.on(RoomEvent.ConnectionQualityChanged, this.onQualityChanged)
  }

  /** True if currently in the suppressed state. UI reads this for the local-side banner. */
  isSuppressed(): boolean {
    return this.suppressed !== null
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    try { this.room.off(RoomEvent.ConnectionQualityChanged, this.onQualityChanged) } catch { /* noop */ }
    this.clearPoorTimer()
    this.clearGoodTimer()
    this.suppressed = null
    // Best-effort metadata reset on teardown so a stale "suppressed"
    // flag doesn't outlive the watcher into the next call.
    try {
      const meta = this.parseMetadata()
      if (meta.bandwidthSuppressed) {
        delete meta.bandwidthSuppressed
        delete meta.bandwidthSuppressedReason
        this.writeMetadata(meta)
      }
    } catch { /* noop */ }
  }

  // Master gate. Auto-suppress is DEFAULT OFF (see file header) — it false-
  // positived (esp. backgrounded-tab throttling) into a camera kill→restore
  // loop. Opt back in with localStorage 'voiceConnQualitySuppress'='on'.
  private suppressionEnabled(): boolean {
    return typeof localStorage !== 'undefined' && localStorage.getItem('voiceConnQualitySuppress') === 'on'
  }

  private onQualityChanged = (quality: ConnectionQuality, participant: { sid?: string }): void => {
    // Filter to local participant only — quality events also fire for
    // remotes which we don't act on (peer suppression is decided by
    // their own watcher and broadcast via THEIR metadata).
    if (participant?.sid !== this.room.localParticipant.sid) return
    if (this.destroyed) return

    // Disabled by default. If a mid-call opt-out leaves us suppressed, recover
    // the camera so nobody is stuck on a black tile.
    if (!this.suppressionEnabled()) {
      this.clearPoorTimer()
      if (this.suppressed) this.scheduleRestore()
      return
    }

    if (quality === ConnectionQuality.Poor) {
      this.clearGoodTimer()
      if (this.suppressed) return // already suppressed, nothing new to do
      // A backgrounded tab throttles timers + the WebRTC send pacer → bursty
      // loss + stale stats → a FALSE Poor. Don't suppress while hidden; LiveKit
      // re-emits quality when the tab wakes, so we re-evaluate then.
      if (typeof document !== 'undefined' && document.hidden) return
      this.schedulePoor('poor')
      return
    }
    if (quality === ConnectionQuality.Lost) {
      // Lost = data plane already gone. Don't wait 5 s of doomed
      // publish — suppress immediately so video tear-down happens
      // while the local stack still has cycles.
      this.clearPoorTimer()
      this.clearGoodTimer()
      if (!this.suppressed) {
        void this.applySuppress('lost')
      }
      return
    }
    // Good or Excellent — the cancel + schedule-restore path.
    this.clearPoorTimer()
    if (!this.suppressed) return // wasn't suppressed; nothing to restore
    this.scheduleRestore()
  }

  private schedulePoor(reason: 'poor' | 'lost'): void {
    this.clearPoorTimer()
    this.poorTimer = setTimeout(() => {
      this.poorTimer = null
      if (this.destroyed) return
      void this.applySuppress(reason)
    }, POOR_DEBOUNCE_MS)
  }

  private scheduleRestore(): void {
    this.clearGoodTimer()
    this.goodTimer = setTimeout(() => {
      this.goodTimer = null
      if (this.destroyed) return
      void this.applyRestore()
    }, GOOD_DEBOUNCE_MS)
  }

  private clearPoorTimer(): void {
    if (this.poorTimer) {
      clearTimeout(this.poorTimer)
      this.poorTimer = null
    }
  }
  private clearGoodTimer(): void {
    if (this.goodTimer) {
      clearTimeout(this.goodTimer)
      this.goodTimer = null
    }
  }

  private async applySuppress(reason: 'poor' | 'lost'): Promise<void> {
    if (this.suppressed) return
    const lp = this.room.localParticipant
    const cameraPub = this.findPub(Track.Source.Camera)
    const screenPub = this.findPub(Track.Source.ScreenShare)
    const cameraWasOn = !!cameraPub && !cameraPub.isMuted
    const screenWasOn = !!screenPub && !screenPub.isMuted
    this.suppressed = { cameraWasOn, screenWasOn }

    console.warn(
      `[connectionQualityWatcher] Local quality ${reason.toUpperCase()} — ` +
        `suppressing ${cameraWasOn ? 'camera ' : ''}${screenWasOn ? 'screen ' : ''}` +
        '(audio kept).',
    )

    // Tear down video + screen. Use LiveKit's typed setters where
    // possible so observers (livekitStore camera flag, etc.) update
    // through the same path as user-driven toggles.
    if (cameraWasOn) {
      try { await lp.setCameraEnabled(false) } catch (err) {
        console.warn('[connectionQualityWatcher] setCameraEnabled(false) failed:', (err as Error)?.message ?? err)
      }
    }
    if (screenWasOn) {
      try { await lp.setScreenShareEnabled(false) } catch (err) {
        console.warn('[connectionQualityWatcher] setScreenShareEnabled(false) failed:', (err as Error)?.message ?? err)
      }
    }

    // Broadcast to peers so their tiles can render the chip. We merge
    // into any existing metadata rather than replace so other state
    // (e.g. raised-hand, away) isn't clobbered.
    try {
      const meta = this.parseMetadata()
      meta.bandwidthSuppressed = ['video', 'screen']
      meta.bandwidthSuppressedReason = reason
      this.writeMetadata(meta)
    } catch (err) {
      console.warn('[connectionQualityWatcher] metadata broadcast failed:', (err as Error)?.message ?? err)
    }

    this.opts.onLocalSuppress?.(reason)
  }

  private async applyRestore(): Promise<void> {
    if (!this.suppressed) return
    const { cameraWasOn } = this.suppressed
    this.suppressed = null
    const lp = this.room.localParticipant

    console.info('[connectionQualityWatcher] Local quality recovered — auto-restoring camera (screen stays off).')

    // Auto-re-enable camera if it was on before. Screen share is
    // user-driven and we don't second-guess; the user re-clicks if
    // they want it back.
    if (cameraWasOn) {
      try { await lp.setCameraEnabled(true) } catch (err) {
        console.warn('[connectionQualityWatcher] auto-restore camera failed:', (err as Error)?.message ?? err)
      }
    }

    // Clear metadata flag so peer chips disappear.
    try {
      const meta = this.parseMetadata()
      delete meta.bandwidthSuppressed
      delete meta.bandwidthSuppressedReason
      this.writeMetadata(meta)
    } catch (err) {
      console.warn('[connectionQualityWatcher] metadata clear failed:', (err as Error)?.message ?? err)
    }

    this.opts.onLocalRestore?.()
  }

  private findPub(source: Track.Source): LocalTrackPublication | undefined {
    const pubs = Array.from(this.room.localParticipant.trackPublications.values())
    return pubs.find((p): p is LocalTrackPublication => p.source === source)
  }

  private parseMetadata(): Record<string, unknown> {
    const raw = this.room.localParticipant.metadata
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  private writeMetadata(meta: Record<string, unknown>): void {
    const next = Object.keys(meta).length ? JSON.stringify(meta) : ''
    try {
      // setMetadata is async + requires `canUpdateOwnMetadata` permission
      // on the participant. We grant that at token-mint time for all
      // users; ignore promise here — the broadcast is best-effort.
      void this.room.localParticipant.setMetadata(next)
    } catch (err) {
      console.warn('[connectionQualityWatcher] setMetadata threw:', (err as Error)?.message ?? err)
    }
  }
}

/**
 * Convenience: parse a remote participant's metadata for the
 * suppression flag so peer tiles can render the chip without
 * re-implementing the JSON shape contract here.
 */
export function readBandwidthSuppression(metadata: string | null | undefined): {
  suppressed: boolean
  reason: 'poor' | 'lost' | null
} {
  if (!metadata) return { suppressed: false, reason: null }
  try {
    const parsed = JSON.parse(metadata)
    if (!parsed || typeof parsed !== 'object') return { suppressed: false, reason: null }
    const flag = parsed.bandwidthSuppressed
    if (!Array.isArray(flag) || flag.length === 0) return { suppressed: false, reason: null }
    const reason = parsed.bandwidthSuppressedReason
    return {
      suppressed: true,
      reason: reason === 'poor' || reason === 'lost' ? reason : null,
    }
  } catch {
    return { suppressed: false, reason: null }
  }
}
